import {
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

const zodCompanyInvoiceReceiversResponseSchema = z.object({
  invoiceReceivers: z.array(z.string()),
});

export type CompanyInvoiceReceiversResponse = z.infer<
  typeof zodCompanyInvoiceReceiversResponseSchema
>;

export type CompanyInvoiceReceivers = { emails: string[] };

/**
 * Fetches company invoice receivers from the API.
 *
 * @param companyId - The ID of the company to fetch.
 * @returns The company invoice receivers, keyed as `invoiceReceivers` per the API response.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_company_invoice_receivers_company__id__invoice_receivers_get
 */
export const fetchCompanyInvoiceReceivers = async (
  companyId: UUID,
  options?: KyRequestOptions,
) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.INVOICE_RECEIVERS.GET_ALL(companyId), {
        validate: { response: zodCompanyInvoiceReceiversResponseSchema },
        ...options,
      })
      .json<CompanyInvoiceReceiversResponse>();
  } catch (error) {
    log.error({
      message: 'Error fetching invoice receivers for company',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query based custom hook for getting the invoice receivers for a company with a given companyId.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook, with the data remapped to `{emails}` for the calling UI.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_company_invoice_receivers_company__id__invoice_receivers_get
 */
export const useQueryCompanyInvoiceReceivers = (
  companyId: UUID,
  options?: Omit<
    UseQueryOptions<
      CompanyInvoiceReceiversResponse,
      Error,
      CompanyInvoiceReceivers
    >,
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  return useQuery<
    CompanyInvoiceReceiversResponse,
    Error,
    CompanyInvoiceReceivers
  >({
    queryFn: companyId
      ? async ({ signal }) =>
          fetchCompanyInvoiceReceivers(companyId, { signal })
      : skipToken,
    queryKey: queryKeysCompany.getInvoiceReceivers(companyId),
    select: (response) => ({ emails: response.invoiceReceivers }),
    ...options,
  });
};
